import type {
  ExecutionsHistoryRouteSection,
  ExecutionsHistoryScheduleSection,
  ExecutionsHistorySectionResult,
} from '@src/models/interfaces/executionsHistoryDashboard.interface';
import { buildScheduleSummaryData } from '../../SchedulesDashboard/components/ScheduleSummary/historyData';
import type { ScheduleSectionsState } from '../../SchedulesDashboard/components/ScheduleSummary/hooks/useScheduleHistorySections';
import type { ScheduleSummaryData, ScheduleSummaryUser } from '../../SchedulesDashboard/components/ScheduleSummary/types';
import type { RouteSectionsState } from '../hooks/useRouteHistorySections';

/** Maps each Routes report section to the Schedule-shaped section the shared builder expects. */
export const ROUTE_TO_SCHEDULE_SECTION: Record<ExecutionsHistoryRouteSection, ExecutionsHistoryScheduleSection> = {
  'route-overview': 'schedule-overview',
  'route-compliance': 'schedule-compliance',
  'route-events': 'schedule-events',
  'route-users': 'schedule-users',
  'route-divisions': 'schedule-divisions',
};

type RouteCompletionRange = Omit<ScheduleSummaryData['scheduleCompletionRanges'][number], 'schedules'> & {
  routes?: ScheduleSummaryData['scheduleCompletionRanges'][number]['schedules'];
  schedules?: ScheduleSummaryData['scheduleCompletionRanges'][number]['schedules'];
};

type RouteUser = ScheduleSummaryUser & { routes?: number };

type RouteUserRange = Omit<ScheduleSummaryData['completionRanges'][number], 'users'> & { users?: RouteUser[] };

type RouteSummarySectionData = Omit<
  Partial<ScheduleSummaryData>,
  'totalSchedules' | 'scheduleCompletionRanges' | 'completionRanges' | 'divisionScheduleAssignments'
> & {
  totalRoutes?: number;
  totalSchedules?: number;
  routeCompletionRanges?: RouteCompletionRange[];
  scheduleCompletionRanges?: RouteCompletionRange[];
  completionRanges?: RouteUserRange[];
  divisionRouteAssignments?: ScheduleSummaryData['divisionScheduleAssignments'];
  divisionScheduleAssignments?: ScheduleSummaryData['divisionScheduleAssignments'];
};

/** Renames Route-only API field names to their Schedule equivalents so the shared builder runs unmodified. */
const toScheduleShapedData = (data: RouteSummarySectionData): Partial<ScheduleSummaryData> => ({
  ...data,
  totalSchedules: data.totalRoutes ?? data.totalSchedules,
  scheduleCompletionRanges: (data.routeCompletionRanges ?? data.scheduleCompletionRanges ?? []).map(range => ({
    ...range,
    schedules: range.routes ?? range.schedules ?? [],
  })),
  completionRanges: data.completionRanges?.map(range => ({
    ...range,
    users: (range.users ?? []).map(user => ({ ...user, schedules: user.routes ?? user.schedules })),
  })),
  divisionScheduleAssignments: data.divisionRouteAssignments ?? data.divisionScheduleAssignments,
});

const toScheduleShapedResult = (
  result?: ExecutionsHistorySectionResult,
): ExecutionsHistorySectionResult | undefined => {
  if (!result || result.status !== 'ok') return result;
  const data = toScheduleShapedData(result.response.data as RouteSummarySectionData);
  return { ...result, response: { ...result.response, data } };
};

/** Adapts the Routes API sections into the shared ScheduleSummary view model. */
export const buildRouteSummaryData = (sections: RouteSectionsState): ScheduleSummaryData => {
  const scheduleShapedSections = Object.fromEntries(
    (Object.entries(ROUTE_TO_SCHEDULE_SECTION) as Array<
      [ExecutionsHistoryRouteSection, ExecutionsHistoryScheduleSection]
    >).map(([routeSection, scheduleSection]) => [
      scheduleSection,
      { ...sections[routeSection], result: toScheduleShapedResult(sections[routeSection].result) },
    ]),
  ) as ScheduleSectionsState;
  return buildScheduleSummaryData(scheduleShapedSections);
};
